import React from 'react'
import cn from 'classnames'
import { observer } from 'mobx-react'
import { GridApi, BodyScrollEvent, GridReadyEvent } from 'ag-grid-community'
import { AgGridReact, AgGridColumn } from 'ag-grid-react'
import { isEqual, difference } from 'lodash'

import ScriptStore from 'stores/script/ScriptStore'
import Script from 'stores/script/Script'
import { ScriptWhereItem } from 'stores/script/ScriptWhere'
import Record from 'stores/record/Record'
import RecordStore from 'stores/record/RecordStore'
import TabStore from 'stores/tab/TabStore'

import createError from 'utils/createError'
import getRecordId from 'utils/getRecordId'

import RelationPill from './RelationPill'
import { TABLE_ROW_HEIGHT } from './Table'
import TableCellHeaderWithSearch from './TableCellHeaderWithSearch'
import TableCellRenderer from './TableCellRenderer'
import { IProps as ITableCellEditorParams } from './TableCellEditor'
import TableEmptyOverlay from './TableEmptyOverlay'

import styles from './relationInput.scss'

interface IProps extends ITableCellEditorParams {
  initialValue: object[]
}

interface IState {
  value: any[] // This is the value of the connected records, so we cannot type it
}

/**
 * This component is going to look unnecessarily complicated, but there is a very good reason for it
 * We want this component to do two major things:
 *
 * 1. Select already connected records after it has loaded
 * 2. Connect / Disconnect records when they are clicked
 * 3. Infinitely load rows as you scroll down
 *
 * The problem arises from the fact that we need `state.value` to always reflect the final value of this field (after connects / disconnects)
 * If there are 1000 already connected records, but the grid has so far loaded only 100 records, how do you calculate the "final value"?
 * You take the initial value array and add / remove the clicked's row's ID right?
 *
 * Okay but how do you differentiate between an automated row click (during initial selection) & a manual row click (by the user)?
 * You cannot set a private variable before automated selection, and unset it afterwards, because Grid events are emitted asynchronously.
 *
 * So we use an (undocumented) parameter in `node.setSelected` that suppresses row select events. Problem is, it only suppresses `selectionChanged` events
 *
 * Okay no issues, we'll use `onSelectionChanged` rather than `onRowSelectionChanged`. Next problem is, the `onSelectionChanged` event does not tell you which row was unselected / selected
 * So that's why we have a `private selectedRecordIds`. By diffing that and `gridApi.getSelectedNodes()`, we can figure out which row was selected / deselected.
 *
 * Once we know which row was selected / deselected, we can remove that row's ID from the state value.
 *
 * So while this may look like a roundabout way of doing things, this is the only way everything works the way we want
 */

class RelationListInput extends React.Component<IProps, IState> {
  private container: React.RefObject<HTMLDivElement> = React.createRef()
  private table: React.RefObject<HTMLDivElement> = React.createRef()

  private loadedScript: Script
  private whereFilterThatFetchesUnconnectedRecords: ScriptWhereItem | null
  private gridApi!: GridApi // ! because it will be assigned in handleGridReady
  private selectedRecordIds: string[]
  // We use this ^ as a cache of sorts to figure out which node was selected / deselected from the inner grid

  constructor(props: IProps) {
    super(props)

    if (!props.field.typeAsModel) {
      throw createError(
        'RelationListInput.constructor',
        'Invalid field.typeAsModel',
        {
          field: props.field.serialize(),
        }
      )
    }

    const { initialValue } = props
    this.state = {
      value: initialValue ?? [],
    }

    this.loadedScript = ScriptStore.add(
      {
        name: null,
        frozen: true,
        modelId: props.field.typeAsModel.id,
        fieldIds: props.field.typeAsModel.fieldIds,
      },
      { skipPersist: true }
    )
    // Add (invalid) where filters for every field. The table's header will fill them out
    this.loadedScript.fields.forEach(f => {
      f.isScalar &&
        this.loadedScript.where.add({
          fieldIds: [f.id],
          operation:
            f.isInt || f.isFloat || f.isDecimal ? 'equals' : 'contains',
          value: null,
        })
    })

    // The following is an optimization: we only fetch records that aren't already connected
    // For uncommitted records, skip this optimization (since we cannot definitively know what the ID of this uncomitted record in the DB is)

    // Reason for why this works is same as in this.handleViewConnections
    const model = props.field.typeAsModel!
    const record = RecordStore.get(props.node.id) // Record / row you are currently editing
    const backRelationField = model.fields.find(
      f => f.isRelation && f.relationName === props.field.relationName
    )

    // Skip for uncommitted records
    if (model && record && backRelationField && record.isCommitted) {
      const whereFilterValue = JSON.stringify(
        record.model.uniqueIdentifiers.reduce((acc: any, f) => {
          acc[f.name] = record.value[f.name]
          return acc
        }, {})
      )
      this.whereFilterThatFetchesUnconnectedRecords = this.loadedScript.where.add(
        {
          fieldIds: [backRelationField.id],
          operation: 'equals', // Actually irrelevant, but needed to please TS
          value: backRelationField.isList
            ? `{ none: ${whereFilterValue} }`
            : `{ NOT: ${whereFilterValue} }`,
        }
      )
    } else {
      this.whereFilterThatFetchesUnconnectedRecords = null
    }

    this.selectedRecordIds = []
  }

  componentWillUnmount() {
    this.props.api?.resetRowHeights() // Set the row height back to normal
    this.props.api?.onRowHeightChanged()
  }

  // Called by ag-grid to get the final value when editing is finished
  getValue = () => this.state.value

  focus = () => {}

  // ag-grid takes over rendering, so it provides this in place of componentDidMount
  afterGuiAttached = async () => {
    if (!this.container.current || !this.table.current) {
      return
    }

    const { api, node, getTableDimensions } = this.props

    if (!api) {
      return
    }

    const parentTableDimensions = getTableDimensions()
    const ownDimensions = this.container.current.getBoundingClientRect()

    const tableHeight = node.rowHeight - TABLE_ROW_HEIGHT

    this.table.current.style.height = `${tableHeight}px` // And then set the table's height to TABLE_HEIGHT
    this.table.current.style.width = `${
      parentTableDimensions.width - 32 - 20
    }px` // 32 is the width of the checkbox, and 20 is for the 10px margin around

    this.table.current.style.left = `${
      parentTableDimensions.left - ownDimensions.left + 32 + 10 // 32 is width of the checkbox row, 10 is the width of the margin
    }px`
  }

  handleGridReady = async (e: GridReadyEvent) => {
    const { value = [] } = this.state
    const { field } = this.props

    this.gridApi = e.api

    await this.loadedScript.run()

    const connectedRecordIds = value.map((v: any) =>
      getRecordId(field.typeAsModel!.id, v)
    )
    this.gridApi.setRowData([
      ...connectedRecordIds.map(rId => RecordStore.get(rId)),
      ...this.loadedScript.records.filter(
        r => !connectedRecordIds.includes(r.id)
      ), // Remove the connected record to avoid duplication
    ])
    this.selectConnectedRecords()
  }

  handleScroll = async (e: BodyScrollEvent) => {
    const { value = [] } = this.state
    const { node, field } = this.props

    if (e.direction === 'horizontal') {
      return
    }

    if (
      this.loadedScript.recordIds.length >= (this.loadedScript.model.count || 0)
    ) {
      // We're already at the end of the list, no need to try and fetch more
      return
    }

    const isSearching = !!Object.values(this.loadedScript.where.values).find(
      w =>
        w.id !== this.whereFilterThatFetchesUnconnectedRecords?.id &&
        w.value !== null &&
        w.value !== ''
    )
    const tableHeight = node.rowHeight - TABLE_ROW_HEIGHT
    const rowCount =
      (isSearching ? 0 : value.length) + this.loadedScript.pagination.take

    // In the following line, we're trying to fetch the next page of records when you're 20 records from the bottom of the list
    if (
      !this.loadedScript.running &&
      e.top + tableHeight >= TABLE_ROW_HEIGHT * (rowCount - 20)
    ) {
      const moreRecords = await this.loadedScript.loadMore()
      const connectedRecordIds = value.map((v: any) =>
        getRecordId(field.typeAsModel!.id, v)
      )
      this.gridApi.applyTransaction({
        add: moreRecords.filter(r => !connectedRecordIds.includes(r.id)),
      })
      this.selectConnectedRecords()
    }
  }

  selectConnectedRecords = async () => {
    // Select already connected records
    const { value = [] } = this.state
    const { field } = this.props

    const connectedRecordIds = value.map((v: any) =>
      getRecordId(field.typeAsModel!.id, v)
    )

    this.selectedRecordIds = [] // Keep cache updated
    this.gridApi.forEachNode(node => {
      if (connectedRecordIds.includes(node.id)) {
        node.setSelected(true, false, true)
        this.selectedRecordIds.push(node.id)
      }
    })
  }

  handleSelectionChanged = () => {
    // Called *only* when a user changes selection. This feels like a quirk that may change.
    // Be sure to add thorough tests for this.

    const { value = [] } = this.state
    const { field } = this.props

    const connectedRecordIds = value.map((v: any) =>
      getRecordId(field.typeAsModel!.id, v)
    )

    const previouslySelectedNodeIds = this.selectedRecordIds
    const currentlySelectedNodeIds = this.gridApi
      .getSelectedNodes()
      .map(n => n.id)

    if (isEqual(previouslySelectedNodeIds, currentlySelectedNodeIds)) {
      return
    }

    let nextSelectedNodeIds: any[]
    if (previouslySelectedNodeIds.length > currentlySelectedNodeIds.length) {
      // Something was deselected
      const deselectedNodeIds = difference(
        previouslySelectedNodeIds,
        currentlySelectedNodeIds
      )
      this.selectedRecordIds = this.selectedRecordIds.filter(
        rId => rId !== deselectedNodeIds[0]
      )
      this.gridApi.getRowNode(deselectedNodeIds[0])?.setSelected(false)
      nextSelectedNodeIds = connectedRecordIds.filter(
        rId => rId !== deselectedNodeIds[0]
      )
    } else {
      // Something was selected
      const selectedNodeIds = difference(
        currentlySelectedNodeIds,
        previouslySelectedNodeIds
      )
      this.selectedRecordIds.push(selectedNodeIds[0])
      this.gridApi.getRowNode(selectedNodeIds[0])?.setSelected(true)
      nextSelectedNodeIds = connectedRecordIds.concat([selectedNodeIds[0]])
    }

    this.setState({
      value: nextSelectedNodeIds.map(rId => RecordStore.get(rId)!.value), // ! is okay because these record will exist (since they'll be created in runQuery)
    })
  }

  handleClickRelation = () => {
    this.props.api?.stopEditing()
  }

  handleSearch = async () => {
    const { value = [] } = this.state
    const { field } = this.props

    // If any filters other than `whereFilterThatFetchesUnconnectedRecords` have a value, you are searching
    const isSearching = !!Object.values(this.loadedScript.where.values).find(
      w =>
        w.id !== this.whereFilterThatFetchesUnconnectedRecords?.id &&
        w.value !== null &&
        w.value !== ''
    )

    if (isSearching) {
      // if searching, don't let the `whereFilterThatFetchesUnconnectedRecords` interfere
      this.whereFilterThatFetchesUnconnectedRecords?.update({ enabled: false })
    } else {
      // Otherwise, enable it
      this.whereFilterThatFetchesUnconnectedRecords?.update({ enabled: true })
    }

    await this.loadedScript.run()

    if (isSearching) {
      this.gridApi.setRowData(this.loadedScript.records)
    } else {
      const connectedRecordIds = value.map((v: any) =>
        getRecordId(field.typeAsModel!.id, v)
      )

      this.gridApi.setRowData([
        ...connectedRecordIds.map(rId => RecordStore.get(rId)),
        ...this.loadedScript.records.filter(
          r => !connectedRecordIds.includes(r.id)
        ), // Remove the connected record to avoid duplication
      ])
    }

    this.selectConnectedRecords()
  }

  handleSkipToUnconnected = () => {
    const { value = [] } = this.state
    const { field } = this.props

    const indexOfLastConnectedRecord = value.length - 1
    if (indexOfLastConnectedRecord === -1) {
      return
    }

    if (!field.typeAsModel) {
      return
    }

    // Scroll to the last connected record.
    // Yes, that means we're not exactly scrolling to the first unconnected record, but getting that to work is not worth it
    const lastConnectedRecord = RecordStore.get(
      getRecordId(field.typeAsModel.id, value[indexOfLastConnectedRecord])
    )

    if (!lastConnectedRecord) {
      return
    }

    const lastConnectedNode = this.gridApi.getRowNode(lastConnectedRecord.id)
    this.gridApi.ensureNodeVisible(lastConnectedNode, 'top')
  }

  handleViewConnections = () => {
    const { field, node } = this.props
    const { value = [] } = this.state

    if (value.length === 0) {
      return
    }

    if (!field.isRelation || !field.typeAsModel) {
      // Should never happen
      return
    }

    const record = RecordStore.get(node.id) // Record / row you are currently editing

    if (!record) {
      // Should never happen
      return
    }

    /**
     * Assume this schema:
     *
     * model User {
     *    id     Int
     *    name   String
     *    posts  Post[]  @relation("UserToPost")
     *
     *    @@id([id, name])
     * }
     *
     * model Post {
     *    id          Int
     *    title       String
     *    authorId    Int
     *    authorName  String
     *    author      User  @relation("PostToUser", fields: [authorId, authorName], references: [id, name])
     *
     *    @@id([id, title])
     * }
     *
     * You just clicked on `Open in New Tab` from the relation editor of user.posts
     *
     * We want to now have this script open in a new tab:
     *
     * prisma.post.findMany({
     *    where: {
     *      author: {
     *        id: 25
     *      }
     *    }
     * })
     *
     * Notes:
     * 1. User.posts is the back-relation
     */

    const backRelationModel = field.typeAsModel // Type of column / cell you are editing
    // Add a where filter that will yield the connected records
    // This is done by equating the back-relation to the currently opened record's IDs (they may be compound too)

    // Find the back relation field
    const backRelationField = backRelationModel.fields.find(
      f => f.isRelation && f.relationName === field.relationName
    ) // This will be `Post.author` according to the example
    if (!backRelationField) {
      return
    }

    const backRelationFieldIdentifier = record.model.uniqueIdentifiers[0]
    // TODO:: We're only using the first identifier. This will be incorrect for compound identifiers.

    // Create a new tab with the back relation model open
    const tab = TabStore.add({
      modelId: backRelationModel.id,
      preview: false,
    })
    tab.session!.script!.where.add({
      fieldIds: [backRelationField.id, backRelationFieldIdentifier.id],
      operation: 'equals',
      value: String(record.value[backRelationFieldIdentifier.name]),
    }) // `!` is fine here because we know it is a script tab

    TabStore.switch({ id: tab.id })
  }

  render() {
    const { value = [] } = this.state
    const { field } = this.props

    const columnDefinitions = field.typeAsModel!.fields

    return (
      <>
        <div ref={this.container} className={styles.container}>
          <RelationPill
            type={field.type}
            isList
            count={value.length}
            isNull={false}
            isPressed
            onClick={this.handleClickRelation}
          />
        </div>

        <div
          data-cy="input--relation-list"
          ref={this.table}
          className={cn(styles.table, 'ag-theme-relations')}
        >
          <AgGridReact
            rowSelection="multiple"
            getRowNodeId={params => (params as Record).id}
            suppressCellSelection
            headerHeight={64}
            frameworkComponents={{
              TableCellRenderer,
              TableCellHeaderWithSearch,
              TableEmptyOverlay,
            }}
            rowMultiSelectWithClick
            onGridReady={this.handleGridReady}
            onBodyScroll={this.handleScroll}
            onSelectionChanged={this.handleSelectionChanged}
            noRowsOverlayComponent="TableEmptyOverlay"
            // We're not using the infinite model here because there are a lot of things we want to do, and it'll just get in the way.
            // Details in the constructor
          >
            <AgGridColumn
              colId="checkbox"
              headerName=""
              editable={false}
              resizable={false}
              sortable={false}
              pinned={true}
              suppressNavigable={true}
              maxWidth={32}
              checkboxSelection={true}
            />
            {columnDefinitions.map(c => (
              <AgGridColumn
                key={c.id}
                editable={false}
                resizable={false}
                sortable={false}
                suppressMovable={true}
                headerComponent="TableCellHeaderWithSearch"
                headerComponentParams={{
                  field: c,
                  script: this.loadedScript,
                  onSearch: this.handleSearch,
                }}
                cellRenderer="TableCellRenderer"
                cellRendererParams={{
                  field: c,
                }}
              />
            ))}
          </AgGridReact>

          <div className={styles.footer}>
            <button
              data-cy="open-in-new-tab"
              className={cn(styles.footerBtn, {
                [styles.disabled]: value.length === 0,
              })}
              onClick={this.handleViewConnections}
            >
              Open in new tab
            </button>
            <button
              className={styles.footerBtn}
              onClick={this.handleSkipToUnconnected}
            >
              Skip to unconnected records
            </button>
          </div>
        </div>
      </>
    )
  }
}

export default observer(RelationListInput)
